import { Button } from '@/components/ds2'
import type { ConfirmOption } from '../../api/assignDriverApi'
import { refusalTitleOf } from '../results/refusalWords'
import { approvalWordsOf } from './approvalWords'
import { answerAssign, confirmRefusal, startAssign, useAssignStore, type PendingAssign } from './assignStore'
import { AssignDriverPicker } from './AssignDriverPicker'
import type { AssignCandidate, OverdueConfirm, OverdueDelivery } from './assignModel'
import { useAssignCandidates } from './useAssignCandidates'

interface AssignDriverPanelProps {
    tabId: string
    loadId: string
    /** The picker is open for this load (the row's "Assign driver" or "Reassign"). */
    picking: boolean
    /** The row's passed delivery date: the picker opens with its choices. */
    overdue?: OverdueDelivery
    onClose: () => void
    /** A refused row's "Pick another truck": the picker opens again under the row. */
    onPickAnother?: () => void
}

/** The card's question under the row: its own text and the two answers the approval takes. */
const QuestionBlock = ({ pending, onAccept, onDecline }: { pending: PendingAssign; onAccept: () => void; onDecline: () => void }) => {
    const undo = pending.params.method === 'unassign'
    const words = approvalWordsOf(pending.question?.message ?? '', pending.params.method)
    return (
        <div role="region" aria-label="Approval needed" className="flex flex-col gap-2 rounded-xl bg-warning-soft p-3 text-xs text-foreground">
            <p className="font-medium">{words.headline}</p>
            {words.bullets.length > 0 && (
                <ul className="flex list-disc flex-col gap-0.5 pl-4">
                    {words.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                    ))}
                </ul>
            )}
            <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={onAccept}>
                    {undo ? 'Unassign' : 'Assign'}
                </Button>
                <Button size="sm" variant="secondary" onClick={onDecline}>
                    {undo ? 'Keep it assigned' : 'Pick another truck'}
                </Button>
            </div>
        </div>
    )
}

/**
 * A row the card refused: its plain title (never the card's message), the confirm buttons the server names (labels as sent), and
 * "Pick another truck".
 */
const RefusalBlock = ({
    pending,
    onConfirm,
    onPickAnother,
}: {
    pending: PendingAssign
    onConfirm: (option: ConfirmOption) => void
    onPickAnother: () => void
}) => (
    <div role="region" aria-label="Assignment refused" className="flex flex-col gap-2 rounded-xl bg-danger-soft p-3 text-xs text-foreground">
        <p>{refusalTitleOf(pending.refusal)}</p>
        <div className="flex flex-wrap gap-2">
            {(pending.refusal?.confirm_choices ?? []).map((option) => (
                <Button key={option.confirm} size="sm" onClick={() => onConfirm(option)}>
                    {option.label}
                </Button>
            ))}
            <Button size="sm" variant="secondary" onClick={onPickAnother}>
                Pick another truck
            </Button>
        </div>
    </div>
)

/** The picker with its trucks read while it is open; a search goes to the server. */
const CandidatePicker = ({ tabId, loadId, overdue, onClose }: { tabId: string; loadId: string; overdue?: OverdueDelivery; onClose: () => void }) => {
    const { candidates, load, error, query, setQuery } = useAssignCandidates(tabId, loadId)
    const onAssign = async (candidate: AssignCandidate, dispatch: boolean, confirm?: OverdueConfirm) => {
        await startAssign(tabId, loadId, { truckId: candidate.truckId, truckLabel: candidate.truckLabel, drivers: candidate.drivers }, dispatch, confirm)
        // The attempt is over: a row it asks about or refuses shows that under itself, and its action is back.
        onClose()
    }
    return (
        <AssignDriverPicker
            candidates={candidates}
            error={error}
            query={query}
            onQueryChange={setQuery}
            onAssign={(candidate, dispatch, confirm) => void onAssign(candidate, dispatch, confirm)}
            onClose={onClose}
            load={load}
            overdue={overdue}
        />
    )
}

/**
 * What an Assign driver row shows under itself: the truck picker while it is open, then whatever the card answered (its question,
 * a refused row's confirm buttons, a failure). Nothing is added to the card's words.
 */
export const AssignDriverPanel = ({ tabId, loadId, picking, overdue, onClose, onPickAnother }: AssignDriverPanelProps) => {
    const pending = useAssignStore((state) => state.pending[loadId])
    const dismiss = () => useAssignStore.getState().setPending(loadId, undefined)

    if (pending?.phase === 'running') return <p className="py-1 text-xs text-foreground-muted">Working…</p>
    if (pending?.phase === 'question')
        return (
            <QuestionBlock
                pending={pending}
                onAccept={() => void answerAssign(tabId, loadId, 'accept')}
                onDecline={() =>
                    void answerAssign(tabId, loadId, 'decline').then(() => {
                        dismiss()
                        // "Pick another truck" reopens the picker under the row; an unassign's "Keep it assigned" does not.
                        if (pending.params.method === 'assign') onPickAnother?.()
                    })
                }
            />
        )
    if (pending?.phase === 'refused')
        return (
            <RefusalBlock
                pending={pending}
                onConfirm={(option) => void confirmRefusal(tabId, loadId, option)}
                onPickAnother={() => {
                    dismiss()
                    onPickAnother?.()
                }}
            />
        )
    if (pending?.phase === 'failed')
        return (
            <p role="alert" className="py-1 text-xs text-danger">
                {pending.message}
            </p>
        )
    if (!picking) return null
    return <CandidatePicker tabId={tabId} loadId={loadId} overdue={overdue} onClose={onClose} />
}
